import { createContext, useContext, useState, useEffect, ReactNode } from "react";

type Language = "en" | "ar";

interface LanguageContextType {
 lang: Language;
 setLang: (lang: Language) => void;
 t: (key: string) => string;
 dir: "ltr" | "rtl";
 isAr: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = () => {
 const ctx = useContext(LanguageContext);
 if (!ctx) throw new Error("useLanguage must be used within LanguageProvider");
 return ctx;
};

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
 const [lang, setLang] = useState<Language>(() => {
 if (typeof window !== "undefined") {
 const urlLang = new URLSearchParams(window.location.search).get("lang");
 if (urlLang === "ar" || urlLang === "en") return urlLang;
 }
 const saved = localStorage.getItem("nil-beauty-lang");
 return (saved === "ar" ? "ar" : "en") as Language;
 });

 const [isTransitioning, setIsTransitioning] = useState(false);

 useEffect(() => {
 localStorage.setItem("nil-beauty-lang", lang);
 document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
 document.documentElement.lang = lang;
 }, [lang]);

 const setLangAnimated = (next: Language) => {
 if (next === lang) return;
 setIsTransitioning(true);
 // Brief blur/fade out, then swap, then settle
 window.setTimeout(() => {
 setLang(next);
 window.setTimeout(() => setIsTransitioning(false), 320);
 }, 180);
 };

 const t = (key: string): string => {
 const translations = lang === "ar" ? ar : en;
 return translations[key] || en[key] || key;
 };

 return (
 <LanguageContext.Provider value={{ lang, setLang: setLangAnimated, t, dir: lang === "ar" ? "rtl" : "ltr", isAr: lang === "ar" }}>
      <div
        key={lang}
        style={
          isTransitioning
            ? {
                transition: "opacity 320ms ease, filter 320ms ease, transform 320ms ease",
                opacity: 0,
                filter: "blur(8px)",
                transform: `translateX(${lang === "ar" ? "12px" : "-12px"})`,
                willChange: "opacity, filter, transform",
              }
            : {
                // No transform/filter/will-change when idle, otherwise this wrapper
                // becomes the containing block for fixed elements (sidebar, navbar).
                transition: "opacity 320ms ease",
                opacity: 1,
              }
        }
      >

 {children}
 </div>
 </LanguageContext.Provider>
 );
};

// ─── English Translations ───
const en: Record<string, string> = {
 // Nav
 "nav.home": "Home",
 "nav.about": "Our Story",
 "nav.shop": "Shop",
 "nav.wholesale": "Wholesale",
 "nav.contact": "Contact",

 // Hero
 "hero.tagline": "Luxury Skincare Collection",
 "hero.title": "Where the Ancient Nile Meets Modern Beauty",
 "hero.subtitle": "Unlock the royal secret to timeless radiance. Handcrafted with nature's most powerful ingredients, inspired by centuries of Egyptian beauty wisdom.",
 "hero.cta": "Explore the Collection",
 "hero.badge.delivery": "Free delivery over EGP 500",
 "hero.badge.offer": "Limited Offer",
 "hero.from": "From",
 "hero.currency": "EGP",
 "hero.shopProduct": "Shop {name}",
 "hero.bestseller": "Bestseller",
 "hero.trust.cod": "Secure InstaPay Checkout",
 "hero.trust.natural": "100% Natural",
 "hero.trust.egypt": "Made in Egypt",

 // Brand Promise
 "promise.natural.title": "Pure Ingredients",
 "promise.natural.desc": "Ethically sourced from the banks of the Nile and beyond",
 "promise.wisdom.title": "Ancient Wisdom",
 "promise.wisdom.desc": "Time-tested beauty secrets of Egyptian queens, perfected for today",
 "promise.science.title": "Modern Luxury",
 "promise.science.desc": "Cutting-edge formulations delivering extraordinary results",

 // Collections
 "collections.tagline": "Our Collections",
 "collections.title": "Curated for Royalty",
 "collections.soaps": "Herbal Soaps",
 "collections.soaps.desc": "Ancient botanicals, modern indulgence",
 "collections.toothpaste": "Natural Oral Care",
 "collections.toothpaste.desc": "Nature's science for a brilliant smile",
 "collections.lip": "Lip & Color",
 "collections.lip.desc": "Regal hues, royal nourishment",
 "collections.moroccan": "Moroccan Black Soap",
 "collections.moroccan.desc": "The authentic hammam ritual",
 "collections.bodycare": "Body Care",
 "collections.bodycare.desc": "Exfoliation & renewal rituals",
 "collections.haircare": "Hair Care",
 "collections.haircare.desc": "Natural nourishment & brilliance",
 "collections.products": "Products",

 // Born From The Nile
 "nile.tagline": "Our Heritage",
 "nile.title": "Born From The Nile",
 "nile.p1": "For millennia, the Nile has nourished great civilizations and inspired beauty rituals worthy of queens. Nil Beauty draws from this eternal river of life, weaving time-honored ingredients like frankincense, olive oil, and miswak into modern skincare science.",
 "nile.p2": "Each product is a tribute to the women who built empires, commanded reverence, and understood that true beauty is both fierce and natural.",
 "nile.cta": "Discover Our Story",

 // Why Nil Beauty
 "why.tagline": "The Nil Difference",
 "why.title": "Why Nil Beauty",
 "why.natural.title": "100% Natural",
 "why.natural.desc": "Free from parabens, sulfates, and synthetic fragrances, always.",
 "why.heritage.title": "Royal Heritage",
 "why.heritage.desc": "Formulas rooted in ancient Egyptian beauty traditions.",
 "why.tested.title": "Clinically Tested",
 "why.tested.desc": "Every formula is dermatologist-tested and approved for safety.",
 "why.results.title": "Visible Results",
 "why.results.desc": "Feel the transformation from the very first application.",

 // Featured
 "featured.tagline": "Bestsellers",
 "featured.title": "Most Loved",
 "featured.viewall": "View All Products",

 // Ramadan
 "ramadan.tagline": "Limited Edition",
 "ramadan.title": "Ramadan Exclusive Offers",
 "ramadan.desc": "Celebrate the blessed month with curated gift sets and exclusive savings on our finest collections. Ideal for thoughtful gifting and self-care rituals.",
 "ramadan.cta": "Shop Ramadan Collection",

 // Testimonials
 "testimonials.tagline": "Love Letters",
 "testimonials.title": "What Our Queens Say",

 // Newsletter
 "newsletter.tagline": "Stay Connected",
 "newsletter.title": "Join the Royal Circle",
 "newsletter.desc": "Be the first to discover new collections, exclusive offers, and timeless beauty secrets.",
 "newsletter.placeholder": "Enter your email",
 "newsletter.button": "Subscribe",
 "newsletter.success.title": "Welcome to Nil Beauty",
 "newsletter.success.desc": "You're now part of our exclusive circle.",

 // Shop
 "shop.tagline": "The Collection",
 "shop.title": "Shop Nil Beauty",
 "shop.empty": "New treasures coming soon to this collection.",
 "shop.all": "All Products",

 // Product Page
 "product.notfound": "Product not found",
 "product.back": "Back to Shop",
 "product.ingredients": "Star Ingredients",
 "product.benefits": "Why You'll Love It",
 "product.addtocart": "Add to Cart",
 "product.added.title": "Added to Cart",
 "product.added.desc": "has been added to your cart.",
 "product.related": "You May Also Love",
 "product.size": "Choose Your Size",
 "product.howtouse": "How to Use",
 "product.details": "Product Details",
 "product.reviews": "Reviews",
 "product.reviews.empty": "Be the first to share your experience with this product!",
 "product.reviews.cta": "Customer reviews will appear here soon.",

 // Cart
 "cart.title": "Your Cart",
 "cart.empty": "Your Cart is Empty",
 "cart.empty.desc": "Explore our luxury collection and find your new favorites.",
 "cart.continueShopping": "Continue Shopping",
 "cart.subtotal": "Subtotal",
 "cart.checkout": "Proceed to Checkout",

 // Checkout
 "checkout.title": "Checkout",
 "checkout.summary": "Order Summary",
 "checkout.name": "Full Name",
 "checkout.phone": "Phone Number",
 "checkout.address": "Delivery Address",
 "checkout.email": "Email (optional)",
 "checkout.emailRequired": "Email",
 "checkout.submit": "Place Order",
 "checkout.error": "Missing Information",
 "checkout.error.required": "Please complete all required fields to proceed.",
 "checkout.success.title": "Order Confirmed!",
 "checkout.success.desc": "Thank you for your order. We'll reach out shortly to confirm your delivery details.",
 "checkout.chooseMethod": "How Would You Like to Order?",
 "checkout.quick": "Quick Checkout",
 "checkout.quick.desc": "Enter your details and pay securely via InstaPay",
 "checkout.whatsapp": "WhatsApp Order",
 "checkout.whatsapp.desc": "Place your order directly through WhatsApp",
 "checkout.details": "Your Details",
 "checkout.changeMethod": "Change method",
 "checkout.paymentMethod": "Payment Method",
 "checkout.cod": "Cash on Delivery",
 "checkout.selectPayment": "Please choose a payment method to continue.",
 "checkout.step.review": "Review",
 "checkout.step.method": "Method",
 "checkout.step.details": "Details",
 "checkout.instapay.title": "Pay with InstaPay",
 "checkout.instapay.link": "Payment Link",
 "checkout.instapay.payNow": "Pay Now",
 "checkout.instapay.screenshot": "Please keep a screenshot of your transaction after completing payment.",
 "checkout.instapay.reminder": "Complete Your Payment",
 "checkout.continue": "Continue",
 "checkout.instapay.scanQR": "Or scan this QR with your InstaPay app",
 "checkout.steps.cart": "Cart",
 "checkout.steps.delivery": "Delivery",
 "checkout.steps.payment": "Payment",
 "checkout.steps.confirmation": "Confirmation",

 // Contact
 "contact.phone": "Phone",

 // About
 "about.tagline": "Our Story",
 "about.title": "The Nil Beauty Journey",
 "about.origin.tagline": "Where It Began",
 "about.origin.title": "Born from the Nile",
 "about.origin.p1": "Nil Beauty was inspired by a deep reverence for the ancient beauty traditions of the Nile Valley. For thousands of years, the women of Egypt and the Nile region have relied on nature's most potent treasures to cultivate their legendary beauty.",
 "about.origin.p2": "We set out to honor these living traditions, not as relics of the past, but as timeless wisdom that deserves a place in modern luxury skincare. Every Nil Beauty product bridges two worlds: heritage and innovation, nature and science.",
 "about.values.tagline": "Our Values",
 "about.values.title": "What We Stand For",
 "about.values.natural": "Natural",
 "about.values.natural.desc": "Beauty should never compromise your health. Every ingredient is thoughtfully chosen from nature's finest offerings.",
 "about.values.luxurious": "Luxurious",
 "about.values.luxurious.desc": "From formulation to finish, every detail is designed to deliver a premium experience worthy of royalty.",
 "about.values.powerful": "Effective",
 "about.values.powerful.desc": "Our products deliver real, visible results, backed by tradition and validated by science.",
 "about.ingredients.tagline": "Key Ingredients",
 "about.ingredients.title": "Nature's Most Powerful Arsenal",
 "about.journey.tagline": "Our Journey",
 "about.journey.title": "Milestones",

 // Wholesale
 "wholesale.tagline": "Partnership",
 "wholesale.title": "Wholesale Inquiries",
 "wholesale.why.tagline": "Why Partner With Us",
 "wholesale.why.title": "Grow with Nil Beauty",
 "wholesale.premium": "Premium Products",
 "wholesale.premium.desc": "Handcrafted luxury that practically sells itself.",
 "wholesale.margins": "Strong Margins",
 "wholesale.margins.desc": "Competitive wholesale pricing for exceptional returns.",
 "wholesale.support": "Dedicated Support",
 "wholesale.support.desc": "We're your partner, not just a supplier.",
 "wholesale.global": "Rising Global Brand",
 "wholesale.global.desc": "A fast-growing name in the luxury beauty space.",
 "wholesale.form.tagline": "Get Started",
 "wholesale.form.title": "Submit Your Inquiry",
 "wholesale.form.company": "Company Name",
 "wholesale.form.contact": "Contact Person",
 "wholesale.form.email": "Email Address",
 "wholesale.form.phone": "Phone Number",
 "wholesale.form.interest": "Products of Interest",
 "wholesale.form.volume": "Estimated Order Volume",
 "wholesale.form.message": "Additional Notes (optional)",
 "wholesale.form.submit": "Submit Inquiry",
 "wholesale.form.success.title": "Inquiry Received",
 "wholesale.form.success.desc": "Our team will be in touch within 24 hours.",

 // Contact
 "contact.tagline": "Get in Touch",
 "contact.title": "Contact Us",
 "contact.reach.tagline": "Reach Out",
 "contact.reach.title": "We'd Love to Hear from You",
 "contact.reach.desc": "Whether you have a question about our products, want to learn about our ingredients, or are interested in a partnership, our team is here for you.",
 "contact.email": "Email",
 "contact.social": "Social",
 "contact.based": "Based In",
 "contact.based.value": "Middle East & Expanding Globally",
 "contact.form.name": "Your Name",
 "contact.form.email": "Your Email",
 "contact.form.subject": "Subject",
 "contact.form.message": "Your Message",
 "contact.form.submit": "Send Message",
 "contact.form.success.title": "Message Sent",
 "contact.form.success.desc": "We'll get back to you shortly.",

 // Footer
 "footer.desc": "Where the Ancient Nile Meets Modern Beauty. Luxury skincare inspired by Egyptian royalty and powered by nature's finest ingredients.",
 "footer.explore": "Explore",
 "footer.ourstory": "Our Story",
 "footer.shopall": "Shop All",
 "footer.collections": "Collections",
 "footer.connect": "Connect",
 "footer.followus": "Follow Us",
 "footer.contactus": "Contact Us",
 "footer.rights": "All rights reserved.",
 "footer.crafted": "Crafted with ancient wisdom & modern luxury",

 // Categories
 "cat.soaps": "Herbal Soaps",
 "cat.toothpaste": "Natural Oral Care",
 "cat.lip-beauty": "Lip & Color",
 "cat.moroccan-black-soap": "Moroccan Black Soap",
 "cat.body-care": "Body Care",
 "cat.hair-care": "Hair Care",

 // Blog
 "blog.tagline": "Nil Beauty Journal",
 "blog.title": "Beauty Wisdom & Natural Living",
 "blog.subtitle": "Expert Articles",
 "blog.heading": "Explore Natural Beauty",
 "blog.readmore": "Read Article",
 "blog.notfound": "Article not found",
 "blog.back": "Back to Journal",
 "blog.relatedProducts": "Featured Nil Beauty Products",
 "nav.blog": "Journal",
};

// ─── Arabic Translations ───
const ar: Record<string, string> = {
 // Nav
 "nav.home": "الرئيسية",
 "nav.about": "قصتنا",
 "nav.shop": "المتجر",
 "nav.wholesale": "الجملة",
 "nav.contact": "تواصلي معنا",

 // Hero
 "hero.tagline": "مجموعة العناية الفاخرة",
 "hero.title": "حيث يلتقي سحر النيل بأسرار الجمال",
 "hero.subtitle": "طقوس جمال مصرية أصيلة، مصنوعة بمكونات طبيعية.",
 "hero.cta": "استكشفي المجموعة",
 "hero.badge.delivery": "شحن مجاني للطلبات فوق ٥٠٠ ج.م",
 "hero.badge.offer": "عرض محدود",
 "hero.from": "يبدأ من",
 "hero.currency": "ج.م",
 "hero.shopProduct": "اطلبي {name}",
 "hero.bestseller": "الأكثر مبيعاً",
 "hero.trust.cod": "دفع آمن عبر InstaPay",
 "hero.trust.natural": "طبيعي ١٠٠٪",
 "hero.trust.egypt": "صُنع في مصر",

 // Brand Promise
 "promise.natural.title": "مكونات نقية",
 "promise.natural.desc": "من خيرات ضفاف النيل وأجود مصادر الطبيعة",
 "promise.wisdom.title": "حكمة الأجداد",
 "promise.wisdom.desc": "أسرار جمال الملكات المصريات، مُطوّرة لعصرنا",
 "promise.science.title": "فخامة عصرية",
 "promise.science.desc": "تركيبات متطورة لنتائج تفوق التوقعات",

 // Collections
 "collections.tagline": "مجموعاتنا",
 "collections.title": "مُنتقاة بعناية للملكات",
 "collections.soaps": "صابون الأعشاب",
 "collections.soaps.desc": "وصفات عريقة بلمسة فاخرة",
 "collections.toothpaste": "العناية الطبيعية بالأسنان",
 "collections.toothpaste.desc": "ابتسامة مشرقة بقوة الطبيعة",
 "collections.lip": "الشفاه والألوان",
 "collections.lip.desc": "ألوان ملكية وعناية مميزة",
 "collections.moroccan": "الصابون المغربي الأسود",
 "collections.moroccan.desc": "تجربة الحمّام الأصيلة",
 "collections.bodycare": "العناية بالجسم",
 "collections.bodycare.desc": "طقوس تقشير وتجديد البشرة",
 "collections.haircare": "العناية بالشعر",
 "collections.haircare.desc": "تغذية طبيعية ولمعان صحي",
 "collections.products": "منتجات",

 // Born From The Nile
 "nile.tagline": "تراثنا",
 "nile.title": "وُلدنا من النيل",
 "nile.p1": "من آلاف السنين والنيل بيغذّي حضارات وبيلهم أسرار جمال تليق بالملكات. نيل بيوتي بتستلهم من نهر الحياة ده, وبتمزج مكونات عريقة زي اللبان وزيت الزيتون والمسواك مع أحدث علوم العناية بالبشرة.",
 "nile.p2": "كل منتج هو تحية للستات اللي حكموا ممالك، وفرضوا احترامهم، وفهموا إن الجمال الحقيقي قوي وطبيعي في نفس الوقت.",
 "nile.cta": "اعرفي قصتنا",

 // Why Nil Beauty
 "why.tagline": "إيه اللي يميّزنا",
 "why.title": "ليه نيل بيوتي؟",
 "why.natural.title": "طبيعي ١٠٠٪",
 "why.natural.desc": "من غير بارابين أو كبريتات أو عطور صناعية, وده وعد مننا.",
 "why.heritage.title": "تراث ملكي",
 "why.heritage.desc": "وصفات من قلب تقاليد الجمال المصرية القديمة.",
 "why.tested.title": "مختبر طبياً",
 "why.tested.desc": "كل تركيبة بتتختبر وبتتعتمد من أطباء جلدية متخصصين.",
 "why.results.title": "نتيجة من أول مرة",
 "why.results.desc": "هتحسّي بالفرق من أول استخدام, مش مجرد كلام.",

 // Featured
 "featured.tagline": "الأكثر مبيعاً",
 "featured.title": "اللي الكل بيحبها",
 "featured.viewall": "شوفي كل المنتجات",

 // Ramadan
 "ramadan.tagline": "إصدار محدود",
 "ramadan.title": "عروض رمضان الحصرية",
 "ramadan.desc": "احتفلي بالشهر الكريم مع مجموعات هدايا مميزة وخصومات حصرية على أجود منتجاتنا. مثالية كهدية أو للعناية بنفسك.",
 "ramadan.cta": "تسوّقي مجموعة رمضان",

 // Testimonials
 "testimonials.tagline": "كلام من القلب",
 "testimonials.title": "ملكاتنا بتقول إيه",

 // Newsletter
 "newsletter.tagline": "خلّيكِ معانا",
 "newsletter.title": "انضمّي للدائرة الملكية",
 "newsletter.desc": "كوني أول واحدة تعرف عن المجموعات الجديدة والعروض الحصرية وأسرار الجمال.",
 "newsletter.placeholder": "اكتبي إيميلك",
 "newsletter.button": "اشتركي",
 "newsletter.success.title": "أهلاً بيكِ في نيل بيوتي",
 "newsletter.success.desc": "بقيتي جزء من عيلتنا الحصرية.",

 // Shop
 "shop.tagline": "المجموعة",
 "shop.title": "تسوّقي نيل بيوتي",
 "shop.empty": "منتجات جديدة في الطريق قريبًا.",
 "shop.all": "كل المنتجات",

 // Product Page
 "product.notfound": "المنتج مش موجود",
 "product.back": "رجوع للمتجر",
 "product.ingredients": "المكونات المميزة",
 "product.benefits": "ليه هتحبيه",
 "product.addtocart": "أضيفي للسلة",
 "product.added.title": "اتضاف للسلة",
 "product.added.desc": "اتضاف لسلة التسوق بتاعتك.",
 "product.related": "ممكن يعجبك كمان",
 "product.size": "اختاري الحجم",
 "product.howtouse": "إزاي تستخدميه",
 "product.details": "تفاصيل المنتج",
 "product.reviews": "آراء العملاء",
 "product.reviews.empty": "كوني أول واحدة تشاركنا تجربتها مع المنتج ده!",
 "product.reviews.cta": "آراء العملاء هتظهر هنا قريبًا.",

 // Cart
 "cart.title": "سلة التسوق",
 "cart.empty": "سلتك فاضية",
 "cart.empty.desc": "استكشفي منتجاتنا الفاخرة وضيفي المفضّل عندك.",
 "cart.continueShopping": "كمّلي تسوّق",
 "cart.subtotal": "الإجمالي",
 "cart.checkout": "إتمام الطلب",

 // Checkout
 "checkout.title": "إتمام الطلب",
 "checkout.summary": "ملخص الطلب",
 "checkout.name": "الاسم بالكامل",
 "checkout.phone": "رقم الموبايل",
 "checkout.address": "عنوان التوصيل",
 "checkout.email": "الإيميل (اختياري)",
 "checkout.emailRequired": "الإيميل",
 "checkout.submit": "أكّدي الطلب",
 "checkout.error": "بيانات ناقصة",
 "checkout.error.required": "كمّلي كل البيانات المطلوبة علشان نقدر نكمّل.",
 "checkout.success.title": "تم تأكيد طلبك! 🎉",
 "checkout.success.desc": "شكراً ليكِ! هنتواصل معاكِ قريب جداً لتأكيد تفاصيل التوصيل.",
 "checkout.chooseMethod": "عايزة تطلبي إزاي؟",
 "checkout.quick": "طلب سريع",
 "checkout.quick.desc": "دخّلي بياناتك وادفعي بأمان عبر InstaPay",
 "checkout.whatsapp": "اطلبي عبر واتساب",
 "checkout.whatsapp.desc": "ابعتي طلبك على طول على واتساب",
 "checkout.details": "بياناتك",
 "checkout.changeMethod": "غيّري الطريقة",
 "checkout.paymentMethod": "طريقة الدفع",
 "checkout.cod": "الدفع عند الاستلام",
 "checkout.selectPayment": "اختاري طريقة الدفع علشان نكمّل.",
 "checkout.step.review": "المراجعة",
 "checkout.step.method": "الطريقة",
 "checkout.step.details": "التفاصيل",
 "checkout.instapay.title": "ادفعي عبر InstaPay",
 "checkout.instapay.link": "لينك الدفع",
 "checkout.instapay.payNow": "ادفعي دلوقتي",
 "checkout.instapay.screenshot": "بعد ما تدفعي، احتفظي بصورة من المعاملة.",
 "checkout.instapay.reminder": "كمّلي الدفع",
 "checkout.continue": "استمري",
 "checkout.instapay.scanQR": "أو امسحي الكود بتطبيق إنستا باي",
 "checkout.steps.cart": "السلة",
 "checkout.steps.delivery": "الشحن",
 "checkout.steps.payment": "الدفع",
 "checkout.steps.confirmation": "التأكيد",

 // Contact
 "contact.phone": "الموبايل",

 // About
 "about.tagline": "قصتنا",
 "about.title": "رحلة نيل بيوتي",
 "about.origin.tagline": "البداية",
 "about.origin.title": "من قلب النيل",
 "about.origin.p1": "نيل بيوتي اتولدت من حب حقيقي لتراث الجمال في وادي النيل. من آلاف السنين، ستات مصر والسودان استخدموا أقوى كنوز الطبيعة علشان يحافظوا على جمالهم الأسطوري.",
 "about.origin.p2": "إحنا بنكرّم التراث ده, مش كحاجة من الماضي، لكن كحكمة حيّة تستاهل مكانها في عالم العناية الفاخرة. كل منتج من نيل بيوتي بيجمع بين الأصالة والحداثة.",
 "about.values.tagline": "قيمنا",
 "about.values.title": "إحنا بنؤمن بإيه",
 "about.values.natural": "طبيعي",
 "about.values.natural.desc": "الجمال مش لازم يكون على حساب صحتك. كل مكون عندنا مختار بعناية من أجود خيرات الطبيعة.",
 "about.values.luxurious": "فاخر",
 "about.values.luxurious.desc": "من التركيبة للتغليف، كل تفصيلة مصممة علشان تحسّي بالفخامة الحقيقية.",
 "about.values.powerful": "فعّال",
 "about.values.powerful.desc": "منتجاتنا مش بس شكلها حلو, دي بتدّي نتائج حقيقية هتشوفيها بعينك.",
 "about.ingredients.tagline": "المكونات",
 "about.ingredients.title": "أقوى كنوز الطبيعة",
 "about.journey.tagline": "رحلتنا",
 "about.journey.title": "محطات مهمة",

 // Wholesale
 "wholesale.tagline": "شراكة",
 "wholesale.title": "استفسارات الجملة",
 "wholesale.why.tagline": "ليه تشتغل معانا",
 "wholesale.why.title": "اكبر مع نيل بيوتي",
 "wholesale.premium": "منتجات فاخرة",
 "wholesale.premium.desc": "فخامة يدوية بتبيع نفسها بنفسها.",
 "wholesale.margins": "ربح عالي",
 "wholesale.margins.desc": "أسعار جملة تنافسية وعوائد ممتازة.",
 "wholesale.support": "دعم متخصص",
 "wholesale.support.desc": "إحنا شركاء، مش مجرد موردين.",
 "wholesale.global": "براند عالمي صاعد",
 "wholesale.global.desc": "اسم بيكبر بسرعة في عالم الجمال الفاخر.",
 "wholesale.form.tagline": "ابدأ دلوقتي",
 "wholesale.form.title": "ابعت استفسارك",
 "wholesale.form.company": "اسم الشركة",
 "wholesale.form.contact": "اسم المسؤول",
 "wholesale.form.email": "الإيميل",
 "wholesale.form.phone": "رقم الموبايل",
 "wholesale.form.interest": "المنتجات اللي تهمك",
 "wholesale.form.volume": "حجم الطلب المتوقع",
 "wholesale.form.message": "أي ملاحظات تانية (اختياري)",
 "wholesale.form.submit": "ابعت الاستفسار",
 "wholesale.form.success.title": "وصلنا استفسارك",
 "wholesale.form.success.desc": "فريقنا هيتواصل معاك خلال ٢٤ ساعة.",

 // Contact
 "contact.tagline": "تواصلي معنا",
 "contact.title": "كلّمينا",
 "contact.reach.tagline": "نحب نسمع منك",
 "contact.reach.title": "إحنا هنا علشانك",
 "contact.reach.desc": "عندك سؤال عن منتجاتنا؟ عايزة تعرفي أكتر عن مكوناتنا؟ مهتمة بشراكة؟ فريقنا موجود ومستنيكِ.",
 "contact.email": "الإيميل",
 "contact.social": "السوشيال ميديا",
 "contact.based": "مكاننا",
 "contact.based.value": "الشرق الأوسط, وبنتوسع عالمياً",
 "contact.form.name": "اسمك",
 "contact.form.email": "إيميلك",
 "contact.form.subject": "الموضوع",
 "contact.form.message": "رسالتك",
 "contact.form.submit": "ابعتي الرسالة",
 "contact.form.success.title": "رسالتك وصلت",
 "contact.form.success.desc": "هنرد عليكِ في أقرب وقت.",

 // Footer
 "footer.desc": "حيث يلتقي سحر النيل بأسرار الجمال. عناية فاخرة بالبشرة مستوحاة من تراث الملكات ومصنوعة من أنقى كنوز الطبيعة.",
 "footer.explore": "استكشفي",
 "footer.ourstory": "قصتنا",
 "footer.shopall": "كل المنتجات",
 "footer.collections": "المجموعات",
 "footer.connect": "تواصلي",
 "footer.followus": "تابعينا",
 "footer.contactus": "كلّمينا",
 "footer.rights": "جميع الحقوق محفوظة.",
 "footer.crafted": "صُنع بحكمة الأجداد وفخامة العصر",

 // Categories
 "cat.soaps": "صابون الأعشاب",
 "cat.toothpaste": "العناية الطبيعية بالأسنان",
 "cat.lip-beauty": "الشفاه والألوان",
 "cat.moroccan-black-soap": "الصابون المغربي الأسود",
 "cat.body-care": "العناية بالجسم",
 "cat.hair-care": "العناية بالشعر",

 // Blog
 "blog.tagline": "مجلة نيل بيوتي",
 "blog.title": "حكمة الجمال والحياة الطبيعية",
 "blog.subtitle": "مقالات متخصصة",
 "blog.heading": "استكشفي الجمال الطبيعي",
 "blog.readmore": "اقرئي المقال",
 "blog.notfound": "المقال مش موجود",
 "blog.back": "رجوع للمجلة",
 "blog.relatedProducts": "منتجات نيل بيوتي المميزة",
 "nav.blog": "المجلة",
};
